import { z } from 'zod'
import { derivedId } from './ids.ts'
import { isoDay, uuid } from './primitives.ts'

/** M16-C: pins, from the running job (benchmark 33, GK-033; docs/M16.md "C"). The person on "Changement des trous" records where each pin went, green by
 *  green, one tap per sector with the club's own words, the offsets in METRES under "Détails"; the planning page keeps the history, the exclusions and the
 *  sheet. Each schema mirrors `core.command_type.payload_schema` in `20261201100200_pins.sql`; the contract check (ops/contracts/check.mjs) runs the same
 *  fixtures and random payloads through both.
 *  - `pin.place@1` is a FACT of the field (flag.record; offline-first, projected in `sync/projectors.pins.ts`; kiosk-allowed by rule 9).
 *  - `pin.correct@1`, `pin_exclusion.set@1`, `pin_exclusion.close@1`, `flag_settings.set@1` are INTENTS of the office (flag.manage; sendAndWait, no projector). */

/** The default grid while the site has no words of its own (the twin of `private.flag_default_sectors`): front / middle / back × left / centre / right, as
 *  codes the screen puts into words, marked "(à confirmer)" until the Superintendent saves his own. Refused by the server once the club's words exist. */
export const DEFAULT_SECTORS = ['front_left', 'front_centre', 'front_right', 'middle_left', 'middle_centre', 'middle_right', 'back_left', 'back_centre', 'back_right'] as const
export type DefaultSector = (typeof DEFAULT_SECTORS)[number]
/** How the default grid is laid out (three across). */
export const DEFAULT_SECTOR_COLUMNS = 3
/** The pace the device converts with while the site has no `pace_m` (the owner's default, "(à confirmer)"); nothing is stored in paces. */
export const DEFAULT_PACE_M = 0.9
/** One sector word: 1..16 characters; at most 16 words. */
export const SECTOR_WORD_MAX = 16
export const SECTORS_MAX = 16
/** A note on a placement, in code points. */
export const PIN_NOTE_MAX = 200
/** An offset in metres (from the front edge, from the left edge as seen from the fairway). */
export const PIN_OFFSET_MAX = 200
/** The sectors of the last `PIN_REPEAT_DAYS` days are flagged on the grid (a sand pill, never a refusal). */
export const PIN_REPEAT_DAYS = 7
/** How many past placements the grid of a green counts. */
export const PIN_HISTORY_COUNT = 14
export const EXCLUSION_REASONS = ['recovery', 'damage', 'work', 'other'] as const
export type ExclusionReason = (typeof EXCLUSION_REASONS)[number]

/** The one settings row of a site: its id is derived from the site, by the device and by the server alike (`private.flag_settings_id`). */
export const flagSettingsId = (siteId: string): string => derivedId(siteId, 'flag_settings')

const sector = z.string().min(1).max(SECTOR_WORD_MAX)
const offset = z.number().min(0).max(PIN_OFFSET_MAX).nullable()
const note = z.string().max(PIN_NOTE_MAX).nullable()
/** The GeoJSON shape as `area.create@1` takes it: checked for SHAPE here and in the database's JSON schema; the polygon's validity is PostGIS's, in the handler. */
const geoJson = z.looseObject({ type: z.string(), coordinates: z.array(z.unknown()) })

/** One tap on a sector, from the job (fact). The day defaults to the command's instant in the site's zone; the offsets are metres, rounded to a tenth. */
export const pinPlaceV1 = z.strictObject({
  placement_id: uuid, area_id: uuid, sector_code: sector, placed_on: isoDay.optional(),
  offset_front_m: offset.optional(), offset_side_m: offset.optional(), note: note.optional(), job_id: uuid.optional(),
})
/** A correction: a new row in the original's place (its green, day, instant and job); the original keeps its values and points to it. */
export const pinCorrectV1 = z.strictObject({
  placement_id: uuid, corrects_placement_id: uuid, sector_code: sector, offset_front_m: offset.optional(), offset_side_m: offset.optional(), note: note.optional(),
})
/** An exclusion created (expected_revision 0) or rewritten whole from the revision read; `valid_to` null = open; `geom` an optional polygon. */
export const pinExclusionSetV1 = z.strictObject({
  exclusion_id: uuid, expected_revision: z.number().int().min(0), area_id: uuid, label: z.string().min(1).max(80), reason: z.enum(EXCLUSION_REASONS),
  valid_from: isoDay, valid_to: isoDay.nullable().optional(), geom: geoJson.optional(),
})
/** The range ends on a day (today by default), once; a repeat is silent. */
export const pinExclusionCloseV1 = z.strictObject({ exclusion_id: uuid, closed_on: isoDay.optional() })
/** The club's words, the layout and the pace; `expected_revision` 0 creates the site's one row, the revision read rewrites it. */
export const flagSettingsSetV1 = z.strictObject({
  expected_revision: z.number().int().min(0), sectors: z.array(sector).min(1).max(SECTORS_MAX), columns: z.number().int().min(1).max(4),
  pace_m: z.number().min(0.3).max(2).nullable().optional(),
})

export type PinPlaceV1 = z.infer<typeof pinPlaceV1>
export type PinCorrectV1 = z.infer<typeof pinCorrectV1>
export type PinExclusionSetV1 = z.infer<typeof pinExclusionSetV1>
export type PinExclusionCloseV1 = z.infer<typeof pinExclusionCloseV1>
export type FlagSettingsSetV1 = z.infer<typeof flagSettingsSetV1>
